import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, X, Plus } from "lucide-react";
import { toast } from "sonner";

const API = "/api";
const DEFAULT_PROMPT = "I'm trying something new — can you tell me one thing you appreciate about me?";
const PLACEHOLDERS = [
  "Someone who's seen you grow",
  "A mentor or coach",
  "Your closest friend",
  "A family member",
  "A colleague who gets you",
];

const emptyRow = () => ({ name: "", email: "" });

const SeedGratitudeDialog = ({ open, onClose, onSent }) => {
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows([emptyRow(), emptyRow(), emptyRow()]);
    setPrompt(DEFAULT_PROMPT);
    setSubmitting(false);
  }, [open]);

  const updateRow = (i, field, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: value };
    setRows(next);
  };

  const addRow = () => {
    if (rows.length >= 10) {
      toast.error("You can seed up to 10 people at a time");
      return;
    }
    setRows([...rows, emptyRow()]);
  };

  const removeRow = (i) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, idx) => idx !== i));
  };

  const filled = rows.filter(r => r.name.trim() && r.email.trim());
  const canSend = filled.length > 0 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/gratitude/seed-asks`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipients: filled.map(r => ({ name: r.name.trim(), email: r.email.trim().toLowerCase() })),
          prompt: prompt.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to send");
      }
      const data = await res.json();
      if (data.sent === 0) {
        const reason = data.skipped?.[0]?.reason;
        toast.error(reason ? `Couldn't send: ${reason}` : "No one got added");
      } else {
        toast.success(
          data.skipped?.length
            ? `Sent to ${data.sent} · skipped ${data.skipped.length}`
            : `Your notes are out there — sent to ${data.sent} ${data.sent === 1 ? "person" : "people"}`
        );
        onSent?.();
        onClose();
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="seed-gratitude-dialog">
        <div className="px-6 pt-5 pb-2">
          <DialogTitle
            className="text-lg font-semibold"
            style={{ color: "var(--color-text-primary)" }}
            data-testid="seed-dialog-title"
          >
            Seed your wall with 3 people who know you well
          </DialogTitle>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            They&apos;ll get a warm note from you — and a chance to reflect back what they see in you.
            Works even if they&apos;re not on Evoluras yet.
          </p>
        </div>

        <div className="px-6 pb-3 space-y-2 max-h-[44vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`seed-row-${i}`}>
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                  placeholder={PLACEHOLDERS[i] || "Their name"}
                  maxLength={80}
                  className="px-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border-default)",
                    color: "var(--color-text-primary)",
                  }}
                  data-testid={`seed-name-${i}`}
                />
                <div className="relative">
                  <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-placeholder)" }} />
                  <input
                    type="email"
                    value={r.email}
                    onChange={(e) => updateRow(i, "email", e.target.value)}
                    placeholder="email"
                    className="w-full pl-8 pr-2 py-2 rounded-lg text-sm outline-none focus:ring-2"
                    style={{
                      background: "var(--color-surface)",
                      border: "1px solid var(--color-border-default)",
                      color: "var(--color-text-primary)",
                    }}
                    data-testid={`seed-email-${i}`}
                  />
                </div>
              </div>
              {rows.length > 1 && (
                <button
                  onClick={() => removeRow(i)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5 flex-shrink-0"
                  style={{ color: "var(--color-text-placeholder)" }}
                  aria-label="Remove row"
                  data-testid={`seed-remove-${i}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          {rows.length < 10 && (
            <button
              onClick={addRow}
              className="text-xs font-medium inline-flex items-center gap-1 mt-2 hover:underline"
              style={{ color: "var(--color-text-interactive)" }}
              data-testid="seed-add-row"
            >
              <Plus size={12} /> Add another person
            </button>
          )}
        </div>

        <div className="px-6 pb-4 pt-2">
          <Label
            className="text-xs mb-2 block font-semibold"
            style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            One shared message
          </Label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            maxLength={280}
            className="resize-none"
            placeholder={DEFAULT_PROMPT}
            data-testid="seed-prompt"
          />
          <p className="text-xs mt-1.5" style={{ color: "var(--color-text-placeholder)" }}>
            Each person gets this same message. They can reply with one honest sentence.
          </p>
        </div>

        <div
          className="flex items-center justify-between gap-3 px-6 py-3"
          style={{ borderTop: "1px solid var(--color-border-default)", background: "var(--color-surface-subtle)" }}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-full h-9 text-sm"
            data-testid="seed-cancel"
          >
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {filled.length} ready
            </span>
            <Button
              onClick={submit}
              disabled={!canSend}
              className="rounded-full h-9 px-5"
              style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
              data-testid="seed-submit"
            >
              {submitting ? "Sending…" : "Send to all"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SeedGratitudeDialog;
